import {
    BS_IMAGE_CAPTION_CLASS,
    BS_IMAGE_CAPTION_TYPE,
    BS_IMAGE_FIGURE_CLASS,
    BS_IMAGE_FIGURE_TYPE,
    BS_IMAGE_IMG_TYPE,
    BS_IMAGE_PLACEHOLDER_SRC,
} from './constants';
import { findCaptionInFigure, getCaptionText } from './utils';

function isPlaceholderImageComponent(component) {
    if (!component) return false;

    const tagName = (component.get('tagName') || '').toLowerCase();
    const type = component.get('type');
    if (type !== BS_IMAGE_IMG_TYPE && tagName !== 'img') return false;

    const src = component.getAttributes?.()?.src || component.view?.el?.getAttribute?.('src') || '';
    if (!src || src === BS_IMAGE_PLACEHOLDER_SRC) return true;

    return Boolean(component.hasClass?.('gjs-plh-image'));
}

function syncImageAttributesFromDom(component) {
    const el = component.view?.el;
    if (!el) return;

    const attrs = component.getAttributes?.() || {};
    const nextAttrs = {};

    if (!attrs.src && el.getAttribute('src')) {
        nextAttrs.src = el.getAttribute('src');
    }
    if (attrs.alt == null && el.hasAttribute('alt')) {
        nextAttrs.alt = el.getAttribute('alt') || '';
    }
    if (attrs.title == null && el.hasAttribute('title')) {
        nextAttrs.title = el.getAttribute('title') || '';
    }

    if (Object.keys(nextAttrs).length) {
        component.addAttributes(nextAttrs);
    }
}

function removeDuplicatePlaceholderImagesFromFigure(figure) {
    if (!figure) return;

    const images = figure.components().filter((child) => {
        const tagName = (child.get('tagName') || '').toLowerCase();
        return child.get('type') === BS_IMAGE_IMG_TYPE || tagName === 'img';
    });

    if (images.length <= 1) return;

    const hasRealImage = images.some((child) => !isPlaceholderImageComponent(child));
    if (!hasRealImage) return;

    images.forEach((child) => {
        if (isPlaceholderImageComponent(child)) {
            child.remove();
        }
    });
}

export function createFigureCaptionModel() {
    return {
        getCaptionComponent() {
            return findCaptionInFigure(this);
        },

        ensureCaptionForEditing() {
            let caption = this.getCaptionComponent();
            if (!caption) {
                caption = this.components().add({ type: BS_IMAGE_CAPTION_TYPE });
            }
            caption.removeClass('sio-image-caption--hidden');
            caption.addClass('sio-image-caption--editing');
            return caption;
        },

        syncCaptionVisibility(isEditing = false) {
            const caption = this.getCaptionComponent();
            const text = getCaptionText(caption);

            if (!text && !isEditing) {
                caption?.remove();
                return;
            }

            const activeCaption =
                caption || this.components().add({ type: BS_IMAGE_CAPTION_TYPE });
            activeCaption.removeClass('sio-image-caption--hidden');

            if (isEditing) {
                activeCaption.addClass('sio-image-caption--editing');
            } else {
                activeCaption.removeClass('sio-image-caption--editing');
            }
        },
    };
}

export function initFigureCaptionModel() {
    if (!this.getClasses().includes(BS_IMAGE_FIGURE_CLASS)) {
        this.addClass(BS_IMAGE_FIGURE_CLASS);
    }

    removeDuplicatePlaceholderImagesFromFigure(this);
}

export function isBsImageFigureElement(el) {
    if (el?.tagName !== 'FIGURE') return false;
    if (el.classList?.contains(BS_IMAGE_FIGURE_CLASS)) return true;

    return Boolean(
        el.querySelector?.('img.sio-image:not(.sio-gallery-image)')
    );
}

export function createFigureDefaults() {
    return {
        'custom-name': 'Figure',
        tagName: 'figure',
        droppable: false,
        draggable: true,
        selectable: false,
        highlightable: false,
        hoverable: false,
        // Enabled so image toolbar move/clone/delete can delegate to the figure.
        removable: true,
        copyable: true,
        layerable: true,
        classes: [BS_IMAGE_FIGURE_CLASS],
        components: [{ type: BS_IMAGE_IMG_TYPE }],
    };
}

export function initCaptionModel() {
    if (!this.getClasses().includes(BS_IMAGE_CAPTION_CLASS)) {
        this.addClass(BS_IMAGE_CAPTION_CLASS);
    }

    this.on('change:content', () => {
        const figure = this.parent();
        if (figure?.get('type') === BS_IMAGE_FIGURE_TYPE) {
            figure.syncCaptionVisibility(true);
        }
    });
}

export function isBsImageCaptionElement(el) {
    if (el?.tagName !== 'FIGCAPTION') return false;
    if (el.classList?.contains(BS_IMAGE_CAPTION_CLASS)) return true;

    const parent = el.parentElement;
    return (
        parent?.tagName === 'FIGURE' &&
        (parent.classList?.contains(BS_IMAGE_FIGURE_CLASS) ||
            parent.querySelector?.('img.sio-image'))
    );
}

export function wrapLegacyImageIfNeeded(editor, component) {
    if (component?.get('type') !== BS_IMAGE_IMG_TYPE) return null;
    if (component.parent()?.get('type') === BS_IMAGE_FIGURE_TYPE) return null;

    const parent = component.parent();
    if (!parent) return null;

    syncImageAttributesFromDom(component);

    const index = parent.components().indexOf(component);
    const figure = parent.components().add(
        { type: BS_IMAGE_FIGURE_TYPE, components: [] },
        { at: index }
    );

    component.move(figure, { at: 0 });
    removeDuplicatePlaceholderImagesFromFigure(figure);
    editor.select(component);
    return figure;
}
